import { Ajuda } from '../../design/Ajuda';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Sessao } from '../../auth';
import { Icone } from '../../design/Icone';
import { ErroDeArea } from '../../design/Estados';
import {
  decidirCapacidade,
  diaCurto,
  lerEquipe,
  lerHistoricoDoProdutoNoGiro,
  lerNaoCabe,
} from '../../giro';
import type {
  ContagemDoItem,
  CorDaConferencia,
  FiltrosDoNaoCabe,
  GiroMaximo,
  HistoricoDoProdutoNoGiro,
  PainelDoNaoCabe,
  ProdutoDoNaoCabe,
} from '../../giro';
import { SubnavDoGiro } from './SubnavDoGiro';

/**
 * NÃO CABE & BLOQUEIOS 3.0 (Carlos, 08/10/2026) — o painel do gestor para decidir o "não cabe na
 * prateleira" sem abrir outra tela:
 *   - indicadores do período (bloqueados, liberados, erros de reposição, divergências abertas,
 *     produtos com "não cabe", produtos que ficaram sem repor);
 *   - busca (nome, código HOS, EAN, fabricante — sem acento), filtros e ordem, paginados no
 *     servidor;
 *   - por produto: GIRO MÁXIMO (o dia em que mais vendeu no período), a última CONTAGEM
 *     (prateleira, o que achou no estoque, o estoque do HOS gravado NA HORA), se precisava repor e
 *     a divergência;
 *   - clique no produto: painel lateral com o histórico e a decisão (confirmar / recusar).
 * Os bloqueios de venda em si moram na tela Bloqueados.
 */

const quando = (iso: string) =>
  new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
const hojeBr = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
const somar = (dia: string, n: number) =>
  new Date(Date.parse(`${dia}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

const COR: Record<CorDaConferencia, string> = {
  VERDE: 'gr-st-ok',
  AMARELO: 'gr-st-rep',
  VERMELHO: 'gr-st-div',
  CINZA: '',
};
const PERIODOS = [7, 15, 30, 60, 90] as const;
const RESULTADO: Record<string, string> = {
  ERRO_BALCONISTA_CONFIRMADO: 'Auditor achou (erro)',
  BALCONISTA_CORRETO: 'Não tinha mesmo (certo)',
  INCONCLUSIVO: 'Inconclusivo',
  PROBLEMA_DADOS: 'Problema de dados',
  PRODUTO_MUDOU_LOCAL: 'Mudou de lugar',
};

export function CapacidadeEBloqueios({ sessao }: { sessao: Sessao }) {
  const hoje = hojeBr();
  const [f, setF] = useState<FiltrosDoNaoCabe>({
    de: somar(hoje, -29),
    ate: hoje,
    situacao: 'PENDENTE',
    ordem: 'DECLARACOES',
    divergencia: 'TODAS',
    giroDias: 30,
    pagina: 1,
  });
  const [texto, setTexto] = useState('');
  const [d, setD] = useState<PainelDoNaoCabe | null>(null);
  const [erro, setErro] = useState(false);
  const [aberto, setAberto] = useState<ProdutoDoNaoCabe | null>(null);
  const [pessoas, setPessoas] = useState<readonly { accountId: string; nome: string }[]>([]);
  const pedido = useRef(0);

  const carregar = useCallback(() => {
    setErro(false);
    const n = ++pedido.current;
    void lerNaoCabe(f).then((r) => {
      if (n !== pedido.current) return; // resposta velha de um filtro anterior
      if (r.ok) setD(r.dados);
      else setErro(true);
    });
  }, [f]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    void lerEquipe().then((r) => r.ok && setPessoas(r.dados.pessoas));
  }, []);
  // busca com folga de digitação; volta para a página 1
  useEffect(() => {
    const t = setTimeout(() => {
      const busca = texto.trim();
      setF((x) => ((x.busca ?? '') === busca ? x : { ...x, busca, pagina: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [texto]);
  const mudar = (p: Partial<FiltrosDoNaoCabe>) =>
    setF((x) => ({ ...x, ...p, pagina: p.pagina ?? 1 }));

  const ind = d?.indicadores;
  const paginas = d ? Math.max(1, Math.ceil(d.total / d.porPagina)) : 1;
  return (
    <section className="cv gr">
      <SubnavDoGiro atual="giro-capacidades" />
      <div className="cv-corpo">
        <div className="cv-titulo cv-titulo-2l">
          <h1>Não cabe &amp; bloqueios</h1>
          <span>
            O que a equipe disse que não cabe na prateleira, com o giro máximo, a contagem × HOS e
            os erros de reposição do período
          </span>
        </div>

        <div className="cv-kpis gr-kpis-6">
          <Kpi
            rotulo="BLOQUEADOS AGORA"
            ajuda="Balconistas com as vendas bloqueadas neste momento por erro de reposição. Toque para ver em Bloqueados."
            valor={ind?.bloqueadosAgora}
            href="#/giro-bloqueados"
          />
          <Kpi
            rotulo="LIBERADOS NO PERÍODO"
            ajuda="Bloqueios que já terminaram (sozinhos ou liberados pelo gestor) no período escolhido."
            valor={ind?.liberadosNoPeriodo}
            href="#/giro-bloqueados"
          />
          <Kpi
            rotulo="ERROS DE REPOSIÇÃO"
            ajuda="Vezes em que o balconista disse que não tinha para repor e o auditor achou o produto."
            valor={ind?.errosDeReposicao}
            sub="o auditor achou"
          />
          <Kpi
            rotulo="DIVERGÊNCIAS ABERTAS"
            ajuda="Casos de divergência ainda sem solução. Toque para abrir Divergências."
            valor={ind?.divergenciasAbertas}
            href="#/divergencias"
          />
          <Kpi
            rotulo='PRODUTOS COM "NÃO CABE"'
            ajuda="Produtos em que alguém disse que não cabe mais na prateleira. O gestor confirma (vira o alvo do produto) ou recusa."
            valor={ind?.produtosComCapacidade}
          />
          <Kpi
            rotulo="FICARAM SEM REPOR"
            ajuda="Produtos que precisavam de reposição e terminaram o Giro sem ser repostos."
            valor={ind?.produtosPrecisandoRepor}
            sub="produtos"
          />
        </div>

        <div className="cv-cartao gr-filtros-nc">
          <label>
            <span className="gr-rotulo">DE</span>
            <input
              type="date"
              className="gr-input"
              value={f.de}
              max={f.ate}
              onChange={(e) => e.target.value && mudar({ de: e.target.value })}
            />
          </label>
          <label>
            <span className="gr-rotulo">ATÉ</span>
            <input
              type="date"
              className="gr-input"
              value={f.ate}
              min={f.de}
              max={hoje}
              onChange={(e) => e.target.value && mudar({ ate: e.target.value })}
            />
          </label>
          <label>
            <span className="gr-rotulo">FUNCIONÁRIO</span>
            <select
              className="gr-input"
              value={f.conta ?? ''}
              onChange={(e) => mudar({ conta: e.target.value || undefined })}
            >
              <option value="">Todos</option>
              {pessoas.map((p) => (
                <option key={p.accountId} value={p.accountId}>
                  {p.nome}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="gr-rotulo">
              SITUAÇÃO
              <Ajuda rotulo="situação">
                Para decidir = ninguém confirmou nem recusou ainda. Confirmada = virou o alvo do
                produto. Recusada = o gestor achou que cabe.
              </Ajuda>
            </span>
            <select
              className="gr-input"
              value={f.situacao}
              onChange={(e) => mudar({ situacao: e.target.value as FiltrosDoNaoCabe['situacao'] })}
            >
              <option value="PENDENTE">Para decidir</option>
              <option value="CONFIRMADA">Confirmadas</option>
              <option value="RECUSADA">Recusadas</option>
              <option value="TODAS">Todas</option>
            </select>
          </label>
          <label>
            <span className="gr-rotulo">DIVERGÊNCIA</span>
            <select
              className="gr-input"
              value={f.divergencia}
              onChange={(e) =>
                mudar({ divergencia: e.target.value as FiltrosDoNaoCabe['divergencia'] })
              }
            >
              <option value="TODAS">Todas</option>
              <option value="COM">Com divergência</option>
              <option value="SEM">Sem divergência</option>
              <option value="FALTOU_NA_REPOSICAO">Faltou na reposição</option>
              <option value="ESTOQUE_INCONSISTENTE">Estoque inconsistente</option>
            </select>
          </label>
          <label>
            <span className="gr-rotulo">
              GIRO MÁXIMO
              <Ajuda rotulo="giro máximo">
                O maior número de unidades que o produto vendeu num único dia nesse período. Ajuda a
                ver se o que cabe na prateleira aguenta um dia forte.
              </Ajuda>
            </span>
            <select
              className="gr-input"
              value={f.giroDias}
              onChange={(e) => mudar({ giroDias: Number(e.target.value) })}
            >
              {PERIODOS.map((n) => (
                <option key={n} value={n}>
                  Últimos {n} dias
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="cv-cartao cv-sem-pad">
          <div className="cv-cartao-topo cv-pad gr-nc-topo">
            <h2>Produtos que disseram que não cabem na prateleira</h2>
            <label className="gr-nc-busca">
              <input
                className="gr-input"
                placeholder="Buscar produto, código HOS ou EAN..."
                aria-label="Buscar produto, código HOS ou EAN"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
              {texto && (
                <button type="button" className="gr-link-botao" onClick={() => setTexto('')}>
                  Limpar
                </button>
              )}
            </label>
            <label>
              <span className="gr-rotulo">ORDENAR</span>
              <select
                className="gr-input"
                value={f.ordem}
                onChange={(e) => mudar({ ordem: e.target.value as FiltrosDoNaoCabe['ordem'] })}
              >
                <option value="DECLARACOES">Mais declarações</option>
                <option value="DIVERGENCIAS">Mais divergências</option>
                <option value="PRODUTO">Produto (A–Z)</option>
                <option value="DATA">Mais recentes</option>
              </select>
            </label>
            <span className="cv-cinza cv-peq">
              {d ? `${String(d.total)} resultado${d.total === 1 ? '' : 's'}` : ''}
            </span>
          </div>
          {erro ? (
            <ErroDeArea oQue="o que não cabe" aoTentar={carregar} />
          ) : !d ? (
            <div className="cv-carregando" aria-busy="true" />
          ) : d.produtos.length === 0 ? (
            <p className="cv-vazio">
              {f.busca ? 'Nenhum produto com essa busca no período.' : 'Nada com esses filtros.'}
            </p>
          ) : (
            <div className="cv-rolagem">
              <table className="cv-tabela gr-tabela-nc">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Disseram que cabem</th>
                    <th>Giro máximo ({d.giro.dias} dias)</th>
                    <th>Última contagem</th>
                    <th>Precisava repor?</th>
                    <th>Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {d.produtos.map((p) => (
                    <tr key={p.productId} className="cv-clicavel" onClick={() => setAberto(p)}>
                      <td>
                        <button type="button" className="gr-link-botao gr-nc-nome">
                          {p.nome}
                        </button>
                        <div className="cv-cinza cv-peq cv-mono">
                          {[p.codigo && `HOS ${p.codigo}`, p.ean].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td className="cv-peq">
                        <b>{p.menorCapacidade}</b> (alvo {p.alvo}) · {p.declaracoesNoPeriodo}{' '}
                        {p.declaracoesNoPeriodo === 1 ? 'vez' : 'vezes'}
                        <div className="cv-cinza">
                          {[...new Set(p.declaracoes.map((x) => x.nome))].join(', ')}
                        </div>
                      </td>
                      <td className="cv-peq">
                        <Giro g={p.giroMaximo} />
                      </td>
                      <td className="cv-peq">
                        <ContagemCurta c={p.ultimaContagem} />
                      </td>
                      <td className="cv-peq">
                        <Necessidade c={p.ultimaContagem} />
                      </td>
                      <td>
                        {p.ultimaContagem ? (
                          <span className={`gr-st ${COR[p.ultimaContagem.conferencia.cor]}`}>
                            {p.ultimaContagem.conferencia.situacao}
                          </span>
                        ) : (
                          <span className="gr-st">Sem contagem</span>
                        )}
                        {p.divergenciasNoPeriodo > 0 && (
                          <div className="cv-cinza cv-peq">
                            {p.divergenciasNoPeriodo} divergência(s) no período
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {d && paginas > 1 && (
            <div className="cv-pad gr-capac-acoes">
              <button
                type="button"
                className="cv-botao"
                disabled={d.pagina <= 1}
                onClick={() => mudar({ pagina: d.pagina - 1 })}
              >
                ◀ Anterior
              </button>
              <span className="cv-peq">
                Página {d.pagina} de {paginas}
              </span>
              <button
                type="button"
                className="cv-botao"
                disabled={d.pagina >= paginas}
                onClick={() => mudar({ pagina: d.pagina + 1 })}
              >
                Próxima ▶
              </button>
            </div>
          )}
        </div>
      </div>
      {aberto && (
        <PainelDoProduto
          sessao={sessao}
          p={aberto}
          giroDias={f.giroDias ?? 30}
          aoFechar={() => setAberto(null)}
          aoDecidir={() => {
            setAberto(null);
            carregar();
          }}
        />
      )}
    </section>
  );
}

function Kpi({
  rotulo,
  valor,
  sub,
  href,
  ajuda,
}: {
  rotulo: string;
  valor: number | undefined;
  sub?: string;
  href?: string;
  ajuda: string;
}) {
  const corpo = (
    <>
      <div className="cv-kpi-rotulo">
        {rotulo}
        <Ajuda rotulo={rotulo.toLowerCase()}>{ajuda}</Ajuda>
      </div>
      <div className="cv-kpi-valor">{valor ?? '—'}</div>
      {sub && <div className="cv-kpi-sub">{sub}</div>}
    </>
  );
  return href ? (
    <a className="cv-kpi gr-kpi-link" href={href}>
      {corpo}
    </a>
  ) : (
    <div className="cv-kpi">{corpo}</div>
  );
}

function Giro({ g }: { g: GiroMaximo | null }) {
  if (!g) return <span className="cv-cinza">Sem venda no período</span>;
  return (
    <>
      <b>{g.qtd} un.</b> em {diaCurto(g.dia)}
      <span className="cv-cinza cv-bloco">
        {g.noPeriodo} un. em {g.diasComVenda} dia(s) com venda
      </span>
    </>
  );
}

function Necessidade({ c }: { c: ContagemDoItem | null }) {
  if (!c) return <>—</>;
  const k = c.conferencia;
  if (k.necessidade === 'SIM')
    return (
      <>
        <b>Sim</b>, {k.sugerido}
      </>
    );
  return <>{k.necessidade === 'NAO' ? 'Não' : '—'}</>;
}

function ContagemCurta({ c }: { c: ContagemDoItem | null }) {
  if (!c) return <span className="cv-cinza">Nunca contado no Giro</span>;
  return (
    <>
      {c.pessoa} · {c.contadoEm ? quando(c.contadoEm) : diaCurto(c.diaGiro)}
      <div className="cv-cinza">
        prateleira {c.prateleira ?? '—'} · achou no estoque {c.achouNoEstoque ?? '—'} · HOS{' '}
        {c.estoqueHos ?? 'não registrado'}
      </div>
    </>
  );
}

/* ------------------------------ painel lateral ------------------------------ */

function PainelDoProduto({
  sessao,
  p,
  giroDias,
  aoFechar,
  aoDecidir,
}: {
  sessao: Sessao;
  p: ProdutoDoNaoCabe;
  giroDias: number;
  aoFechar: () => void;
  aoDecidir: () => void;
}) {
  const [h, setH] = useState<HistoricoDoProdutoNoGiro | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    void lerHistoricoDoProdutoNoGiro(p.productId, giroDias).then((r) =>
      r.ok ? setH(r.dados) : setErro(true),
    );
  }, [p.productId, giroDias]);
  useEffect(carregar, [carregar]);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);
  const pendentes = p.declaracoes.filter((x) => x.estado === 'PENDENTE');
  return (
    <div className="cv-veu" onClick={aoFechar}>
      <aside
        className="cv-gaveta"
        role="dialog"
        aria-label={`Histórico de ${p.nome}`}
        onClick={(ev) => ev.stopPropagation()}
      >
        <header>
          <button type="button" className="cv-fechar" aria-label="Fechar" onClick={aoFechar}>
            <Icone nome="x" tamanho={15} traco={1.8} />
          </button>
          <h2>{p.nome}</h2>
          <p>
            {[
              p.codigo && `Código HOS ${p.codigo}`,
              p.ean && `EAN ${p.ean}`,
              h?.produto.categoria ?? h?.produto.grupo,
              p.fabricante,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </header>
        <div className="cv-gaveta-corpo">
          {erro ? (
            <ErroDeArea oQue="o histórico do produto" aoTentar={carregar} />
          ) : !h ? (
            <div className="cv-carregando" aria-busy="true" />
          ) : (
            <>
              <h3>Giro máximo · últimos {h.giro.dias} dias</h3>
              <p className="cv-peq">
                <Giro g={h.giroMaximo} />
              </p>

              {pendentes.length > 0 && <Decisao sessao={sessao} p={p} aoDecidir={aoDecidir} />}

              <h3>Contagens</h3>
              {h.contagens.length === 0 ? (
                <p className="cv-vazio">Nunca contado no Giro.</p>
              ) : (
                <ul className="gr-nc-contagens">
                  {h.contagens.map((c) => (
                    <Contagem key={c.itemId} c={c} />
                  ))}
                </ul>
              )}

              <h3>Declarações de &quot;não cabe&quot;</h3>
              <ul className="gr-capac-quem">
                {h.declaracoes.map((x) => (
                  <li key={x.id}>
                    <b>{x.nome}</b> disse que cabem <b>{x.capacidade}</b> (alvo {x.alvo}, tinha{' '}
                    {x.prateleira}) · {quando(x.em)} ·{' '}
                    <span
                      className={`gr-st ${x.estado === 'CONFIRMADA' ? 'gr-st-ok' : x.estado === 'RECUSADA' ? 'gr-st-div' : 'gr-st-rep'}`}
                    >
                      {x.estado === 'PENDENTE'
                        ? 'Para decidir'
                        : x.estado === 'CONFIRMADA'
                          ? 'Confirmada'
                          : 'Recusada'}
                    </span>
                    {x.decisao && (
                      <div className="cv-cinza cv-peq">
                        {x.decisao.por} em {quando(x.decisao.em)}: {x.decisao.motivo}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

const ESTOQUE: Record<ContagemDoItem['conferencia']['estoque'], string> = {
  COMPATIVEL: 'Compatível com o HOS',
  PRATELEIRA_MAIOR_QUE_HOS: 'Prateleira maior que o HOS (divergência de estoque)',
  NAO_ACHOU_O_QUE_HOS_TEM: 'Disse que não achou o que o HOS tinha',
  SEM_REGISTRO: 'Estoque histórico não registrado',
};

function Contagem({ c }: { c: ContagemDoItem }) {
  const k = c.conferencia;
  return (
    <li className="gr-nc-contagem">
      <div className="gr-capac-topo">
        <div>
          <b>{c.pessoa}</b>{' '}
          <span className="cv-cinza cv-peq">
            · Giro de {diaCurto(c.diaGiro)} ({c.setor})
            {c.contadoEm ? ` · contou ${quando(c.contadoEm)}` : ''}
          </span>
        </div>
        <span className={`gr-st ${COR[k.cor]}`}>{k.situacao}</span>
      </div>
      <dl className="gr-nc-numeros">
        <div>
          <dt>Prateleira</dt>
          <dd>{c.prateleira ?? '—'}</dd>
        </div>
        <div>
          <dt>Achou no estoque</dt>
          <dd>{c.achouNoEstoque ?? '—'}</dd>
        </div>
        <div>
          <dt>Total visto</dt>
          <dd>{k.visto ?? '—'}</dd>
        </div>
        <div>
          <dt>HOS na contagem</dt>
          <dd>{c.estoqueHos ?? 'não registrado'}</dd>
        </div>
        <div>
          <dt>Precisava repor?</dt>
          <dd>
            <Necessidade c={c} />
          </dd>
        </div>
        <div>
          <dt>Repôs</dt>
          <dd>{c.reposto ?? '—'}</dd>
        </div>
      </dl>
      <p className="cv-cinza cv-peq">
        {ESTOQUE[k.estoque]}
        {k.diferenca !== null && k.estoque !== 'SEM_REGISTRO'
          ? ` · visto − HOS = ${k.diferenca > 0 ? '+' : ''}${String(k.diferenca)} (o estoque de trás não é contado inteiro)`
          : ''}
        {c.capacidadeDeclarada !== null
          ? ` · disse que só cabem ${String(c.capacidadeDeclarada)}`
          : ''}
      </p>
      {c.divergencia && (
        <p className="cv-peq">
          Divergência{' '}
          <a href={`#/caso?id=${c.divergencia.id}`}>
            {c.divergencia.numero !== null
              ? `#${String(c.divergencia.numero).padStart(4, '0')}`
              : 'abrir'}
          </a>{' '}
          ({c.divergencia.tipo === 'ESTOQUE_INCONSISTENTE' ? 'estoque' : 'reposição'}):{' '}
          {c.divergencia.resultado
            ? `${RESULTADO[c.divergencia.resultado] ?? c.divergencia.resultado}${c.divergencia.auditor ? ` · ${c.divergencia.auditor}` : ''}`
            : 'esperando o auditor'}
        </p>
      )}
    </li>
  );
}

function Decisao({
  sessao,
  p,
  aoDecidir,
}: {
  sessao: Sessao;
  p: ProdutoDoNaoCabe;
  aoDecidir: () => void;
}) {
  const [cap, setCap] = useState(String(p.menorCapacidade));
  const [motivo, setMotivo] = useState('');
  const [falha, setFalha] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const decidir = (decisao: 'CONFIRMAR' | 'RECUSAR') => {
    setFalha(null);
    setEnviando(true);
    void decidirCapacidade(sessao, p.productId, {
      decisao,
      capacidade: decisao === 'CONFIRMAR' ? Number(cap || 0) : null,
      motivo: motivo.trim(),
    }).then((r) => {
      setEnviando(false);
      if (r.tipo === 'confirmada') aoDecidir();
      else if (r.tipo === 'recusada') setFalha(r.mensagem);
      else setFalha('Sem resposta do servidor: recarregue antes de repetir.');
    });
  };
  return (
    <>
      <h3>Decidir</h3>
      <div className="gr-capac-acoes">
        <label>
          <span className="gr-rotulo">CABEM</span>
          <input
            className="gr-input gr-input-curto"
            inputMode="numeric"
            value={cap}
            onChange={(e) => setCap(e.target.value.replace(/\D/g, ''))}
          />
        </label>
        <label className="gr-capac-motivo">
          <span className="gr-rotulo">MOTIVO</span>
          <input
            className="gr-input"
            value={motivo}
            maxLength={300}
            placeholder="Ex.: pote grande, conferi na gôndola"
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>
        <button
          type="button"
          className="cv-botao-primario"
          disabled={enviando || motivo.trim().length < 5}
          onClick={() => decidir('CONFIRMAR')}
        >
          Confirmar: cabem {cap || '—'}
        </button>
        <button
          type="button"
          className="cv-botao"
          disabled={enviando || motivo.trim().length < 5}
          onClick={() => decidir('RECUSAR')}
        >
          Recusar (cabe {p.alvo})
        </button>
      </div>
      {falha && (
        <p className="cv-falha" role="alert">
          {falha}
        </p>
      )}
    </>
  );
}
